/**
 * Build My Own Deal — shared state hook.
 *
 * One source of truth for the wizard. State persists to localStorage so
 * guests can refresh the page without losing their progress, and so the
 * "Edit from Review" affordance can jump between steps without losing
 * the answers they've already entered.
 *
 * Strict guardrails (DO NOT CHANGE):
 *   • No math lives here. Pricing/tax/fees/rebate math stays in
 *     /src/data/finance.js, /src/components/PaymentCalculator.js, and
 *     /src/components/RebateSelector.js. We only read the resulting
 *     numbers and store the customer's selections.
 *   • Account-required actions (save & submit) call /api/byod/save and
 *     /api/leads respectively. Guests can only build, not save.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const STORAGE_KEY = "ssc_byod_state_v3";

export const STEPS = [
  { key: "start",               label: "Start" },
  { key: "vehicle",             label: "Vehicle" },
  { key: "trade",               label: "Trade" },
  { key: "money_down",          label: "Money Down" },
  { key: "rebates",             label: "Rebates" },
  { key: "rate",                label: "Rate" },
  { key: "accessories",         label: "Accessories" },
  { key: "required_protection", label: "Protection" },
  { key: "optional_protection", label: "Optional" },
  { key: "sales_tax",           label: "Sales Tax" },
  { key: "review",              label: "Review" },
  { key: "submit",              label: "Submit" },
];

const DEFAULT_STATE = {
  // Step 1 — Start
  identity: {
    mode: null,            // "guest" | "account"
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  },
  // Step 3 — Trade
  trade: {
    has_trade: null,       // "yes" | "no" | "not_sure"
    year: "",
    make: "",
    model: "",
    mileage: "",
    payoff: "",
    appraisal_low: "",
    appraisal_high: "",
    selected_value: null,  // "low" | "mid" | "high"
  },
  // Step 4 — Money Down
  money_down: {
    choice: null,          // "0" | "1000" | "2500" | "5000" | "custom" | "not_sure"
    custom_amount: "",
  },
  // Step 5 — Rebates (mirrors RebateSelector output)
  rebates: {
    selected: [],
    not_sure: [],
    skipped: [],
    total_savings_estimated: 0,
    acknowledged: false,
  },
  // Step 6 — Rate / Credit
  rate: {
    credit_tier: "good",         // perfect | good | ok | bad — matches CREDIT_TIERS in finance.js
    term: 72,                    // months — only used if no Special APR override
    special_apr_id: null,        // incentive_id of the chosen Special APR offer
    special_apr_pct: null,       // APR % override (e.g. 1.99)
    special_apr_term: null,      // term in months override (e.g. 36)
    special_apr_name: null,      // display label
  },
  // Step 7 — Accessories
  accessories: [],               // [{ name, part_number, amount }]
  accessory_site_interest: false, // customer clicked "Shop Chevrolet Accessories" link
  // Step 8 — Required Protection (Paint+Fabric, GAP, Service Contract)
  required_protection: {
    states: {},                  // slug → { status: "accepted"|"declined"|null, finalPrice, decline_step: 0|1|2 }
    expanded: {},                // slug → "learn"|null
  },
  // Step 9 — Optional Protection (the 8 optional items)
  optional_protection: {
    states: {},                  // slug → { status, finalPrice }
    expanded: {},                // slug → "learn"|null
  },
  // Step 10 — Sales Tax / County
  sales_tax: {
    county: "Franklin",          // default — confirmed by user
  },
  // Step 12 — Submit (contact details)
  contact: {
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    preferred_contact: "Text",
    best_time: "",
    notes: "",
  },
  // Wizard meta — used for dirty tracking, "save and come back later",
  // and the "saved vehicle" pin. NEVER read for math.
  meta: {
    bound_vin: null,            // VIN this wizard state is locked to
    last_saved_at: null,        // ISO timestamp of last successful server save
    last_saved_signature: null, // JSON-stringified deal signature when last saved
    opened_saved_deal: false,   // true when user intentionally opened a saved deal
  },
};

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeStorage(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* ignore quota / private-mode failures */ }
}

export function useByodState() {
  const [state, setState] = useState(() => readStorage() || DEFAULT_STATE);
  const [stepIdx, setStepIdx] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY + "_step");
      const n = raw ? Number(raw) : 0;
      return Number.isFinite(n) && n >= 0 && n < STEPS.length ? n : 0;
    } catch { return 0; }
  });

  // Auto-persist on every change
  const persistTimer = useRef(null);
  useEffect(() => {
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => writeStorage(state), 80);
    return () => persistTimer.current && clearTimeout(persistTimer.current);
  }, [state]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY + "_step", String(stepIdx)); } catch { /* ignore */ }
  }, [stepIdx]);

  const update = useCallback((path, patch) => {
    setState((prev) => {
      if (!path) return { ...prev, ...patch };
      return { ...prev, [path]: { ...prev[path], ...patch } };
    });
  }, []);

  const replaceSection = useCallback((path, value) => {
    setState((prev) => ({ ...prev, [path]: value }));
  }, []);

  const reset = useCallback(() => {
    setState(DEFAULT_STATE);
    setStepIdx(0);
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_KEY + "_step");
    } catch { /* ignore */ }
  }, []);

  const goTo = useCallback((idxOrKey) => {
    if (typeof idxOrKey === "string") {
      const i = STEPS.findIndex((s) => s.key === idxOrKey);
      if (i >= 0) setStepIdx(i);
    } else if (typeof idxOrKey === "number") {
      setStepIdx(Math.max(0, Math.min(STEPS.length - 1, idxOrKey)));
    }
  }, []);

  const next = useCallback(() => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1)), []);
  const back = useCallback(() => setStepIdx((i) => Math.max(0, i - 1)), []);

  // Derived numeric helpers
  const moneyDownAmount = useMemo(() => {
    const c = state.money_down?.choice;
    if (c === "0") return 0;
    if (c === "1000") return 1000;
    if (c === "2500") return 2500;
    if (c === "5000") return 5000;
    if (c === "custom") return Number(state.money_down?.custom_amount || 0) || 0;
    return 0;
  }, [state.money_down]);

  const tradeAppraisalValues = useMemo(() => {
    const lo = Number(state.trade?.appraisal_low || 0);
    const hi = Number(state.trade?.appraisal_high || 0);
    if (!lo || !hi || hi <= lo) return { low: 0, mid: 0, high: 0, valid: false };
    const low = Math.round(lo);
    const high = Math.max(0, Math.round(hi - 500));
    const mid = Math.round((lo + hi) / 2);
    return { low, mid, high, valid: true };
  }, [state.trade]);

  const tradeSelectedValue = useMemo(() => {
    if (state.trade?.has_trade !== "yes") return 0;
    const v = state.trade?.selected_value;
    if (v === "low") return tradeAppraisalValues.low;
    if (v === "mid") return tradeAppraisalValues.mid;
    if (v === "high") return tradeAppraisalValues.high;
    return 0;
  }, [state.trade, tradeAppraisalValues]);

  const accessoriesTotal = useMemo(
    () => (state.accessories || []).reduce((s, a) => s + (Number(a.amount) || 0), 0),
    [state.accessories]
  );

  const coveragesTotal = useMemo(() => {
    const req = state.required_protection?.states || {};
    const opt = state.optional_protection?.states || {};
    const sum = (states) =>
      Object.values(states).reduce(
        (s, v) => s + (v?.status === "accepted" && Number(v.finalPrice) ? Number(v.finalPrice) : 0),
        0
      );
    return sum(req) + sum(opt);
  }, [state.required_protection, state.optional_protection]);

  const requiredProtectionComplete = useMemo(() => {
    const states = state.required_protection?.states || {};
    return ["paint-fabric-defense", "gap-protection", "vehicle-service-contract"].every(
      (slug) => states[slug]?.status === "accepted" || states[slug]?.status === "declined"
    );
  }, [state.required_protection]);

  // Deal signature — used to detect unsaved/changed work. Intentionally
  // excludes identity/contact since those don't materially change the
  // deal numbers (and entering your first name in Step 1 shouldn't mark
  // the wizard dirty).
  const dealSignature = useMemo(() => JSON.stringify({
    trade: state.trade,
    money_down: state.money_down,
    rebates: state.rebates,
    rate: state.rate,
    accessories: state.accessories,
    accessory_site_interest: state.accessory_site_interest,
    required: state.required_protection,
    optional: state.optional_protection,
    tax: state.sales_tax,
    step: stepIdx,
  }), [state, stepIdx]);

  // Has the customer touched anything beyond opening the page + entering
  // a first name? Anything other than the bare defaults counts.
  const hasMeaningfulWork = useMemo(() => {
    const s = state;
    return Boolean(
      s.trade?.has_trade ||
      s.money_down?.choice ||
      (s.rebates?.selected?.length || 0) > 0 ||
      (s.rebates?.not_sure?.length || 0) > 0 ||
      (s.rebates?.skipped?.length || 0) > 0 ||
      s.rate?.special_apr_id ||
      (s.accessories || []).length > 0 ||
      s.accessory_site_interest ||
      Object.values(s.required_protection?.states || {}).some((v) => v?.status) ||
      Object.values(s.optional_protection?.states || {}).some((v) => v?.status) ||
      (s.sales_tax?.county && s.sales_tax.county !== "Franklin") ||
      stepIdx > 1
    );
  }, [state, stepIdx]);

  const isDirty = useMemo(() => {
    if (!hasMeaningfulWork) return false;
    return dealSignature !== (state.meta?.last_saved_signature || null);
  }, [hasMeaningfulWork, dealSignature, state.meta?.last_saved_signature]);

  const markSaved = useCallback(() => {
    setState((prev) => ({
      ...prev,
      meta: {
        ...(prev.meta || {}),
        last_saved_at: new Date().toISOString(),
        last_saved_signature: JSON.stringify({
          trade: prev.trade,
          money_down: prev.money_down,
          rebates: prev.rebates,
          rate: prev.rate,
          accessories: prev.accessories,
          accessory_site_interest: prev.accessory_site_interest,
          required: prev.required_protection,
          optional: prev.optional_protection,
          tax: prev.sales_tax,
          step: stepIdx,
        }),
      },
    }));
  }, [stepIdx]);

  return {
    state,
    setState,
    stepIdx,
    stepKey: STEPS[stepIdx]?.key,
    update,
    replaceSection,
    reset,
    goTo,
    next,
    back,
    moneyDownAmount,
    tradeAppraisalValues,
    tradeSelectedValue,
    accessoriesTotal,
    coveragesTotal,
    requiredProtectionComplete,
    isDirty,
    hasMeaningfulWork,
    markSaved,
  };
}
